import React, { useState, useEffect } from 'react';
import { Users, Trophy, Calendar, PlayCircle } from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch dashboard data');

      const data = await response.json();
      setStats(data.stats);
      setRecentMatches(data.recentMatches);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Teams"
          value={stats?.totalTeams || 0}
          icon={<Users className="text-blue-600" size={32} />}
          bgColor="bg-blue-50"
        />
        <StatCard
          title="Total Players"
          value={stats?.totalPlayers || 0}
          icon={<Users className="text-green-600" size={32} />}
          bgColor="bg-green-50"
        />
        <StatCard
          title="Total Matches"
          value={stats?.totalMatches || 0}
          icon={<Trophy className="text-purple-600" size={32} />}
          bgColor="bg-purple-50"
        />
        <StatCard
          title="Upcoming Matches"
          value={stats?.upcomingMatches || 0}
          icon={<Calendar className="text-orange-600" size={32} />}
          bgColor="bg-orange-50"
        />
      </div>

      {/* Live Matches Alert */}
      {stats?.liveMatches > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <PlayCircle className="text-red-600 mr-3" size={24} />
            <p className="text-red-700 font-medium">
              {stats.liveMatches} match{stats.liveMatches > 1 ? 'es' : ''} currently live
            </p>
          </div>
        </div>
      )}

      {/* Recent Matches */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Recent Matches</h2>
        </div>
        <div className="divide-y">
          {recentMatches.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No recent matches
            </div>
          ) : (
            recentMatches.map((match) => (
              <div key={match.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{match.home_team.name}</span>
                      <span className="text-2xl font-bold mx-4">
                        {match.home_score ?? '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{match.away_team.name}</span>
                      <span className="text-2xl font-bold mx-4">
                        {match.away_score ?? '-'}
                      </span>
                    </div>
                  </div>
                  <div className="ml-6 text-right">
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      match.status === 'live' ? 'bg-red-100 text-red-800' :
                      match.status === 'finished' ? 'bg-gray-100 text-gray-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {match.status.toUpperCase()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {match.tournament.name}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(match.match_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, bgColor }) => (
  <div className={`${bgColor} rounded-lg p-6`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-3xl font-bold">{value}</p>
      </div>
      {icon}
    </div>
  </div>
);

export default Dashboard;
